// src/components/wellfinder/VerdictBar.jsx
//
// The write surface. Buttons mirror the keys one-for-one — the keyboard is the
// throughput path and the buttons are how it is discovered, so anything only
// one of them can do is a bug.
//
// NO CONFIRMATION DIALOGS. At three seconds a well, a dialog doubles the
// interaction. The undo toast is the safety net, and it is a better one: reopen
// withdraws the verdict, keeps the values, and leaves an event, where a modal
// only ever prevented a click.

import { useEffect, useRef, useState } from "react";
import { useWfQueueStore, adoptable, adoptRank } from "../../stores/useWfQueueStore";
import { api10FromRrcApi } from "./apiNumber";
import { holderWording } from "./holderWording";
import {
  WF_FLAG_REASONS,
  WF_FLAG_REASON_REQUIRING_NOTE,
} from "../../constants/wfFlagReasons";
import { WF_REJECT_REASONS } from "../../constants/wfRejectReasons";

// Moved to constants/wfRejectReasons.js so RecordFacts can name the same
// reason without a second copy of the list.
const REJECT_REASONS = WF_REJECT_REASONS;

function Key({ children }) {
  return (
    <kbd className="rounded border border-white/20 bg-white/[0.06] px-1 text-[10px] leading-4 text-fg-secondary">
      {children}
    </kbd>
  );
}

function Btn({ onClick, disabled, children, tone = "neutral", testid, title }) {
  const tones = {
    primary: "bg-accent-action/15 text-accent-action ring-accent-action/40 hover:bg-accent-action/25",
    danger: "bg-accent-red/10 text-accent-red ring-accent-red/40 hover:bg-accent-red/20",
    neutral: "bg-white/[0.04] text-fg-secondary ring-white/15 hover:bg-white/[0.08]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={[
        "flex items-center gap-1.5 rounded px-2 py-1 text-xs ring-1 transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-40",
        tones[tone],
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/** What the commit will actually write, in the form it will be stored. */
function CommitPreview({ candidate, hasCandidates, equipmentHint }) {
  if (!candidate && hasCandidates) {
    // Nothing selected and nothing pre-matched. It used to preview rank 1,
    // which the approve then adopted; now the approve asks instead.
    return (
      <p data-testid="commit-preview" className="text-[11px] text-fg-secondary/70">
        No candidate adopted — select one (<Key>1</Key>–<Key>9</Key>), or{" "}
        <span className="text-fg-secondary">Approved – No Candidate</span> writes no registry match.
      </p>
    );
  }
  if (!candidate) {
    return (
      <p data-testid="commit-preview" className="text-[11px] text-fg-secondary/70">
        Approving writes <span className="text-fg-secondary">no registry match</span> — a
        wellhead with no RRC record, which is the Tier C finding.
      </p>
    );
  }
  const api10 = api10FromRrcApi(candidate.rrc_api);
  const hasStatus = adoptable(candidate, "rrc_status_norm");
  const hasCode = adoptable(candidate, "rrc_oil_gas_code");
  return (
    <div data-testid="commit-preview" className="space-y-0.5 text-[11px] text-fg-secondary/70">
      <div>
        {/* THE 10-DIGIT FORM, and on its own line because it is the one value in
            the block an analyst may need to check. The candidate card shows the
            registry's 8-character identifier because that is what appears in
            RRC systems; the record stores ten. Confirming one and storing the
            other is how a wrong identifier reaches a deliverable, so the
            derivation stays visible. */}
        api10{" "}
        <span className="font-mono tabular-nums text-fg-primary">{api10 ?? "—"}</span>
        <span className="ml-1 text-fg-secondary/50">
          (from RRC <span className="font-mono">{candidate.rrc_api}</span>)
        </span>
      </div>
      {/* The other three on ONE line. Each had a line of its own, which cost
          three rows of a panel the command bar was already crowding, to say
          three short things. flex-wrap so a narrow pane folds rather than
          overflowing. */}
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
        <span>
          well_status{" "}
          {hasStatus
            ? <span className="text-fg-secondary">{candidate.rrc_status_norm}</span>
            // Not "null" and not blank: the field is unavailable to adopt, and
            // saying so is different from saying the value is empty.
            : <span className="italic text-fg-secondary/50">registry has none — not written</span>}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          oil_gas_code{" "}
          {hasCode
            ? <span className="text-fg-secondary">{candidate.rrc_oil_gas_code}</span>
            : <span className="italic text-fg-secondary/50">registry has none — not written</span>}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          equipment_type{" "}
          {equipmentHint
            ? <span className="text-fg-secondary">{equipmentHint}</span>
            : <span className="italic text-fg-secondary/50">— set by the commit key</span>}
        </span>
      </div>
    </div>
  );
}

function CommitError({ err, onSkip, onReload, onDismiss }) {
  if (!err) return null;
  const claimed = err.kind === "claimed_by_other";
  return (
    <div
      data-testid={`commit-error-${err.kind}`}
      className="mb-2 rounded border border-accent-red/40 bg-accent-red/10 px-2.5 py-2 text-xs"
    >
      <p className="mb-1.5 text-accent-red">
        {claimed ? (
          <>
            {err.holder} is reviewing {err.onHolder ? "the record that holds this well" : "this record"}
            {err.heldForSeconds != null && ` (${Math.floor(err.heldForSeconds / 60)} min)`}.
          </>
        ) : err.kind === "holder_changed" ? (
          <>The record holding this well changed since you loaded it. Reload to see who holds it now.</>
        ) : err.kind === "holder_escalated" ? (
          <>
            The record holding this well is escalated. Moving a well out of an
            escalated record is a supervisor&rsquo;s decision.
          </>
        ) : err.kind === "contention" ? (
          <>Another change landed at the same moment. Reload and try again.</>
        ) : err.kind === "stale" ? (
          <>This record changed while you were on it.</>
        ) : err.kind === "not_reviewer" ? (
          <>
            You do not have the <code>{err.requiredRole}</code> realm role, which
            adjudicating requires. Reading the queue does not. Ask a platform
            admin to grant it, then sign out and back in — an existing session
            keeps the old claims and will keep failing.
          </>
        ) : err.kind === "not_org" ? (
          <>
            Adjudication is carried out by DreamHammer only. Switch out of the
            impersonated organization to continue.
          </>
        ) : err.kind === "rrc_taken" ? (
          <>
            That registry well is already matched to another record
            {err.conflictingTier ? ` (tier ${err.conflictingTier.toUpperCase()})` : ""}.
            Choose a different candidate, or reject as a duplicate detection.
          </>
        ) : (
          err.message
        )}
      </p>
      {/* Neither of these loses the note or the selected candidate — see the
          commit action. Both 409s are recoverable and the input is the
          expensive part. */}
      <div className="flex gap-2">
        {claimed ? (
          <Btn onClick={onSkip} testid="commit-error-skip">Skip to next</Btn>
        ) : err.kind === "not_reviewer" || err.kind === "not_org" || err.kind === "holder_escalated" ? (
          // NO RELOAD. A permissions failure is not a stale read, and offering
          // the concurrency affordance sends the analyst round a loop that
          // cannot terminate — nothing this page can do fixes it.
          null
        ) : err.kind === "rrc_taken" ? (
          // No action button: the analyst resolves it on the candidate list,
          // which is already on screen. A button here would have to guess which
          // candidate they meant.
          null
        ) : (
          <Btn onClick={onReload} testid="commit-error-reload">Reload and re-decide</Btn>
        )}
        <Btn onClick={onDismiss}>Dismiss</Btn>
      </div>
    </div>
  );
}

/**
 * Moving a well here from the record that holds it. Revealed by approving a
 * taken candidate (or by the holder chip), confirmed by approving again — the
 * same keys, so the keyboard path needs nothing new. Says what happens to the
 * OTHER record before anything is written, because that is the part the
 * analyst cannot see from here.
 */
function TransferConfirm({ confirm, candidate, note, onNote, onApprove, onCancel, committing }) {
  const w = holderWording(candidate);
  if (!w) return null;
  const decided = candidate.taken_by_status !== "pending";
  const approves = [
    ["pump_jack", "p", "Move and approve · pump jack"],
    ["frac_well", "f", "Move and approve · frac well"],
    [null, "a", "Move and approve"],
  ];
  return (
    <div
      data-testid="transfer-confirm"
      className="mb-2 rounded border border-yellow-300/40 bg-yellow-300/[0.06] px-2.5 py-2 text-xs"
    >
      <p className="text-fg-primary">
        Move this well here from <span className="break-all font-mono">{w.id}</span>?
      </p>
      <p className="mt-0.5 text-[11px] text-fg-secondary">
        {w.kind === "matched"
          ? "An analyst matched it there"
          : w.kind === "assigned"
            ? "It was assigned there automatically"
            : "It is held there"}{" "}
        ({w.detail}).{" "}
        {decided
          ? "Moving it reopens that record — its verdict is withdrawn — and it goes back to the queue without a match."
          : "That record goes back to the queue without a match."}
      </p>
      <label className="mt-1.5 block">
        <span className="mb-0.5 block text-[10px] font-mono uppercase tracking-wide text-fg-secondary/70">
          Why is this detection the well? — {decided ? "required" : "optional"}, recorded on the other record
        </span>
        <textarea
          autoFocus
          data-testid="transfer-note"
          value={note}
          onChange={(e) => onNote(e.target.value)}
          rows={2}
          className="w-full rounded border border-bg-border bg-white/[0.03] px-2 py-1 text-xs text-fg-primary outline-none focus:ring-1 focus:ring-accent-info/40"
        />
      </label>
      {confirm.needNote && !note.trim() && (
        <p data-testid="transfer-note-required" className="text-[11px] text-accent-action">
          A reason is required: moving the well withdraws a verdict on the other record.
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {(confirm.opts
          ? approves.filter(([eq]) => eq === (confirm.opts.equipmentType ?? null))
          : approves
        ).map(([eq, key, label]) => (
          <Btn
            key={key}
            tone="primary"
            testid={`transfer-approve-${eq ?? "plain"}`}
            disabled={committing || (decided && !note.trim())}
            onClick={() => onApprove(eq)}
          >
            <Key>{key}</Key> {label}
          </Btn>
        ))}
        <Btn testid="transfer-cancel" onClick={onCancel}>
          <Key>Esc</Key> Cancel
        </Btn>
      </div>
    </div>
  );
}

export default function VerdictBar() {
  const detail = useWfQueueStore((s) => s.detail);
  const committing = useWfQueueStore((s) => s.committing);
  const commitError = useWfQueueStore((s) => s.commitError);
  const selectedRank = useWfQueueStore((s) => s.selectedRank);
  const candidatePrompt = useWfQueueStore((s) => s.candidatePrompt);
  const noCandidateArmed = useWfQueueStore((s) => s.noCandidateArmed);
  const armNoCandidate = useWfQueueStore((s) => s.armNoCandidate);
  const noteDraft = useWfQueueStore((s) => s.noteDraft);
  const setNoteDraft = useWfQueueStore((s) => s.setNoteDraft);
  const rejectPickerOpen = useWfQueueStore((s) => s.rejectPickerOpen);
  // Local, unlike rejectPickerOpen: nothing outside this component reacts to
  // the flag picker being open, and the keymap has no shortcut that needs it.
  const [flagPickerOpen, setFlagPickerOpen] = useState(false);
  const [flagReason, setFlagReason] = useState(WF_FLAG_REASONS[0][0]);
  const setRejectPickerOpen = useWfQueueStore((s) => s.setRejectPickerOpen);
  const reopenOpen = useWfQueueStore((s) => s.reopenOpen);
  const setReopenOpen = useWfQueueStore((s) => s.setReopenOpen);
  const geomMode = useWfQueueStore((s) => s.geomMode);
  const boundaryMode = useWfQueueStore((s) => s.boundaryMode);
  const boundaryDraft = useWfQueueStore((s) => s.boundaryDraft);
  const setBoundaryMode = useWfQueueStore((s) => s.setBoundaryMode);
  const revertBoundary = useWfQueueStore((s) => s.revertBoundary);
  // From the server, and the SAME resolution the write uses — not a second
  // opinion about whether this record may edit this polygon.
  const boundaryEditable = useWfQueueStore((s) => s.detail?.footprint?.boundary_editable === true);
  const boundaryCorrected = useWfQueueStore((s) => s.detail?.footprint?.boundary_corrected === true);
  const boundaryCreatable = useWfQueueStore((s) => s.detail?.footprint?.boundary_creatable === true);
  const seedBoundaryDraft = useWfQueueStore((s) => s.seedBoundaryDraft);
  const setGeomMode = useWfQueueStore((s) => s.setGeomMode);
  const geomDraft = useWfQueueStore((s) => s.geomDraft);
  const commit = useWfQueueStore((s) => s.commit);
  const advance = useWfQueueStore((s) => s.advance);
  const loadDetail = useWfQueueStore((s) => s.loadDetail);
  const clearCommitError = useWfQueueStore((s) => s.clearCommitError);
  const transferConfirm = useWfQueueStore((s) => s.transferConfirm);
  const transferNote = useWfQueueStore((s) => s.transferNote);
  const setTransferNote = useWfQueueStore((s) => s.setTransferNote);
  const cancelTransfer = useWfQueueStore((s) => s.cancelTransfer);

  const noteRef = useRef(null);
  useEffect(() => {
    // `n` focuses the note. Registered here rather than in the page handler so
    // the ref does not have to be threaded through two components.
    // On an adjudicated record the only note that can be attached is a reopen
    // reason, so `n` opens that form rather than doing nothing. autoFocus on
    // the textarea takes it from there.
    const fn = () => {
      const st = useWfQueueStore.getState();
      const rec = st.detail?.record;
      const adjudicated = rec && rec.status !== "pending" && rec.status !== "needs_review";
      if (adjudicated && !st.reopenOpen) { st.setReopenOpen(true); return; }
      noteRef.current?.focus();
    };
    window.addEventListener("wf:focus-note", fn);
    return () => window.removeEventListener("wf:focus-note", fn);
  }, []);

  if (!detail?.record) return null;
  const r = detail.record;
  const cands = detail.candidates ?? [];
  // The store's own rule, so the preview names what the approve would adopt.
  const rank = adoptRank(detail, selectedRank);
  const cand = cands.find((c) => c.rank === rank) ?? null;
  const done = r.status !== "pending" && r.status !== "needs_review";

  return (
    <div className="border-t border-bg-border bg-bg-soft/60 px-4 py-2.5">
      <CommitError
        err={commitError}
        onSkip={() => { clearCommitError(); advance(); }}
        onReload={() => { clearCommitError(); loadDetail(r.id); }}
        onDismiss={clearCommitError}
      />

      {done ? (
        // Reopen, surfaced where a verdict would be. It clears the review
        // fields, keeps the values and their provenance, and downgrades the
        // published correlation to unverified.
        <div className="text-xs">
          <div className="flex items-center gap-3">
            <span className="text-fg-secondary">
              Already {r.status}
              {r.reviewed_by_name ? ` by ${r.reviewed_by_name}` : ""}.
            </span>
            {!reopenOpen && (
              // Step one. No input on screen until the analyst has chosen to
              // reopen — a required "why" in front of someone who has not
              // decided there is a why is a question about nothing.
              <Btn testid="reopen-open" onClick={() => setReopenOpen(true)}>
                Reopen&hellip;
              </Btn>
            )}
            {reopenOpen && (
              <span className="text-fg-secondary/60">
                Reopening withdraws the verdict and keeps the values and their
                provenance. <Key>Esc</Key> to cancel.
              </span>
            )}
          </div>

          {reopenOpen && (
            // Step two. Same shape as the reject reason picker: the action is
            // chosen, then the thing it needs appears.
            <div data-testid="reopen-form" className="mt-2">
              <label className="block">
                <span className="mb-0.5 block text-[10px] font-mono uppercase tracking-wide text-fg-secondary/70">
                  Why is this being reopened? — required
                </span>
                <textarea
                  ref={noteRef}
                  autoFocus
                  data-testid="reopen-note"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={2}
                  className="w-full rounded border border-bg-border bg-white/[0.03] px-2 py-1 text-xs text-fg-primary outline-none focus:ring-1 focus:ring-accent-info/40"
                />
              </label>
              <div className="mt-1.5 flex items-center gap-2">
                <Btn
                  testid="reopen"
                  disabled={!noteDraft.trim()}
                  onClick={() => useWfQueueStore.getState().reopenCurrent(noteDraft)}
                  title={noteDraft.trim() ? "Withdraw the verdict" : "A reason is required"}
                >
                  Reopen
                </Btn>
                <Btn testid="reopen-cancel" onClick={() => setReopenOpen(false)}>
                  Cancel
                </Btn>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {transferConfirm && cand && transferConfirm.rank === cand.rank && (
            <TransferConfirm
              confirm={transferConfirm}
              candidate={cand}
              note={transferNote}
              onNote={setTransferNote}
              onApprove={(eq) => commit("approved", eq ? { equipmentType: eq } : {})}
              onCancel={cancelTransfer}
              committing={committing}
            />
          )}
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <Btn tone="primary" testid="approve-pump_jack" disabled={committing}
                 onClick={() => commit("approved", { equipmentType: "pump_jack" })}>
              <Key>p</Key> Approve · pump jack
            </Btn>
            <Btn tone="primary" testid="approve-frac_well" disabled={committing}
                 onClick={() => commit("approved", { equipmentType: "frac_well" })}>
              <Key>f</Key> Approve · frac well
            </Btn>
            <Btn testid="approve-plain" disabled={committing}
                 onClick={() => commit("approved", {})}
                 title="Approve without asserting an equipment type">
              <Key>a</Key> Approve
            </Btn>
            <Btn testid="approve-no-candidate" disabled={committing || !cands.length}
                 tone={noCandidateArmed ? "primary" : "neutral"}
                 onClick={armNoCandidate}
                 title={cands.length
                   ? "None of the candidates is this well: approve with no registry match"
                   : "There are no candidates to decline: approve it"}>
              <Key>0</Key> Approved – No Candidate
            </Btn>
            <Btn tone="danger" testid="reject-open" disabled={committing}
                 onClick={() => setRejectPickerOpen(!rejectPickerOpen)}>
              <Key>r</Key> Reject…
            </Btn>
            <Btn testid="flag-open" disabled={committing}
                 onClick={() => setFlagPickerOpen(!flagPickerOpen)}>
              <Key>x</Key> Flag&hellip;
            </Btn>
            <Btn testid="geom-mode" onClick={() => setGeomMode(!geomMode)}
                 tone={geomMode ? "primary" : "neutral"}>
              <Key>m</Key> {geomMode ? "Moving — drag the point" : "Move point"}
            </Btn>
            {geomDraft && (
              <span className="text-[11px] text-accent-action">
                geometry will be corrected on commit
              </span>
            )}

            {/* ── DISABLED WITH THE REASON, NOT ENABLED AND FAILING ──────────
                commitRecord resolves the boundary through the record's OWN
                entity link and 409s when there is none. Correct, and useless:
                by the time it arrives the reviewer has entered the mode, placed
                four corners and submitted, and gets back a detection id. Half a
                minute of work gone with no explanation — a conflict with no
                route out.

                Not a corner case: 26 of the 50 records on shared boundaries
                display a polygon they cannot edit.

                The copy names the other RECORD, not the entity or the link. A
                reviewer has no model of the entity plane and needs none to
                understand that this polygon belongs to a neighbour. */}
            {/* ── CREATE, ONLY WHERE THERE IS NOTHING ────────────────────────
                Shown only when the record's own entity holds no boundary AND no
                reviewer has drawn one — the same two conditions commitRecord
                checks, so the button cannot offer what the write refuses.

                39 of 10,800 records. 16 have no polygon anywhere near them; the
                other 23 sit inside a NEIGHBOUR's boundary, which is why this
                replaces the disabled "belongs to another record" state for
                those rather than sitting beside it. They were never blocked
                because they could not have a boundary — only because they had
                no boundary of their own to edit. */}
            {boundaryCreatable && (
              <Btn testid="boundary-create" disabled={committing}
                   onClick={seedBoundaryDraft}
                   tone={boundaryMode ? "primary" : "neutral"}>
                {boundaryMode ? "Drawing — drag the corners" : "Create boundary"}
              </Btn>
            )}

            {!boundaryCreatable && (
            <Btn
              testid="boundary-mode"
              disabled={committing || !boundaryEditable}
              title={boundaryEditable
                ? undefined
                : "This boundary belongs to another record — edit it there"}
              onClick={() => setBoundaryMode(!boundaryMode)}
              tone={boundaryMode ? "primary" : "neutral"}
            >
              <Key>b</Key> {boundaryMode ? "Editing — drag the corners" : "Edit boundary"}
            </Btn>
            )}

            {/* Offered whenever a correction is in play — staged or already
                committed — because a mis-edit is exactly when it is needed and
                the reviewer must not have to guess that redrawing is the wrong
                recovery. Stages the model's polygon verbatim; see
                revertBoundary. */}
            {/* Never on a created boundary: revert restores the MODEL's geometry
                and the model drew none here. The server refuses it; the UI must
                not offer it. */}
            {(boundaryDraft || boundaryCorrected) && !boundaryCreatable
              && !useWfQueueStore.getState().boundaryCreate && (
              <Btn testid="boundary-revert" disabled={committing}
                   onClick={revertBoundary}>
                Revert to model
              </Btn>
            )}
            {boundaryDraft && (
              <span className="text-[11px] text-accent-action">
                boundary will be corrected on commit
              </span>
            )}
          </div>

          {rejectPickerOpen && (
            // A reason is REQUIRED — the server 400s without one — so reject
            // cannot be a single key and the picker is not optional chrome.
            <div data-testid="reject-reasons" className="mb-2 flex flex-wrap gap-1.5">
              {REJECT_REASONS.map(([value, label], i) => (
                <Btn key={value} tone="danger" testid={`reject-${value}`} disabled={committing}
                     onClick={() => commit("rejected", { rejectReason: value })}>
                  <Key>{i + 1}</Key> {label}
                </Btn>
              ))}
            </div>
          )}

          {flagPickerOpen && (
            // A PULLDOWN, not a row of buttons like reject. Six reasons with
            // sentence-length labels would wrap the bar onto three lines, and
            // the labels are what a supervisor reads in the flagged queue —
            // shortening them to fit buttons would cost the thing they are for.
            //
            // A reason is REQUIRED by the server, so this is not optional
            // chrome; the flag key opens it rather than committing.
            <div data-testid="flag-reasons" className="mb-2 space-y-1.5">
              <label className="block">
                <span className="mb-0.5 block text-[10px] font-mono uppercase tracking-wide text-fg-secondary/70">
                  Why is this being escalated? — required
                </span>
                <select
                  autoFocus
                  data-testid="flag-reason"
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  disabled={committing}
                  className="w-full rounded border border-bg-border bg-bg px-2 py-1 text-xs text-fg-primary outline-none focus:ring-1 focus:ring-accent-info/40"
                >
                  {WF_FLAG_REASONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>

              {flagReason === WF_FLAG_REASON_REQUIRING_NOTE && !noteDraft.trim() && (
                // Surfaces WHY the button is disabled. The server enforces this
                // too — "other" names only the absence of a recognised
                // situation, so without a note it escalates a record and says
                // nothing about why.
                <p className="text-[11px] text-accent-action">
                  A note is required when the reason is &ldquo;Other&rdquo;.
                </p>
              )}

              <div className="flex items-center gap-2">
                <Btn
                  testid="flag"
                  disabled={
                    committing
                    || (flagReason === WF_FLAG_REASON_REQUIRING_NOTE && !noteDraft.trim())
                  }
                  onClick={() => commit("flagged", { flagReason })}
                >
                  Flag for review
                </Btn>
                <Btn testid="flag-cancel" onClick={() => setFlagPickerOpen(false)}>
                  Cancel
                </Btn>
              </div>
            </div>
          )}

          {/* ARMED: THE EQUIPMENT, AS A NORMAL APPROVE STATES IT. The keys are
              the normal approve's own; the server refuses a No Candidate that
              states none. */}
          {noCandidateArmed && (
            <div data-testid="no-candidate-equipment" className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-accent-action">Approved – No Candidate: the equipment?</span>
              <Btn tone="primary" testid="nc-pump_jack" disabled={committing}
                   onClick={() => commit("approved", { noCandidate: true, equipmentType: "pump_jack" })}>
                <Key>p</Key> pump jack
              </Btn>
              <Btn tone="primary" testid="nc-frac_well" disabled={committing}
                   onClick={() => commit("approved", { noCandidate: true, equipmentType: "frac_well" })}>
                <Key>f</Key> frac well
              </Btn>
              <Btn testid="nc-unclear" disabled={committing}
                   onClick={() => commit("approved", { noCandidate: true, equipmentType: null })}>
                <Key>a</Key> unclear
              </Btn>
              <Btn testid="nc-cancel" onClick={armNoCandidate}>
                <Key>Esc</Key> Cancel
              </Btn>
            </div>
          )}
          {candidatePrompt && !cand && (
            <p data-testid="candidate-prompt" className="mb-1 text-[11px] text-accent-action">
              Pick a candidate (<Key>1</Key>–<Key>9</Key>) to approve with it, or use Approved – No Candidate
              (<Key>0</Key>) if none of them is this well.
            </p>
          )}
          <CommitPreview candidate={cand} hasCandidates={cands.length > 0} equipmentHint={null} />
        </>
      )}

      {/* The optional verdict note, on the PENDING path only. The reopen note
          is a different thing — required, and revealed by choosing to reopen —
          so it lives in that form rather than being one field doing both jobs
          with a changing label. */}
      {!done && (
        <label className="mt-2 block">
          <span className="mb-0.5 block text-[10px] font-mono uppercase tracking-wide text-fg-secondary/70">
            Note <Key>n</Key> — optional, kept if the commit fails
          </span>
          <textarea
            ref={noteRef}
            data-testid="note-input"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={2}
            className="w-full rounded border border-bg-border bg-white/[0.03] px-2 py-1 text-xs text-fg-primary outline-none focus:ring-1 focus:ring-accent-info/40"
          />
        </label>
      )}
    </div>
  );
}
